"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Phone, ArrowRight, HardHat, Building2, House as Home, Hammer } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const servicePillars = [{
  icon: Home,
  title: "Residential",
  description: "Extensions, loft conversions, and private renovations across Clapham and SW4."
}, {
  icon: Building2,
  title: "Commercial",
  description: "Main contractor support, retail fit-outs, and multi-storey access structures."
}, {
  icon: Hammer,
  title: "Roofing",
  description: "Safe working platforms, solar access, chimney stacks, and temporary roofs."
}, {
  icon: HardHat,
  title: "Specialist access",
  description: "Bespoke cantilevered scaffolds, tight urban plots, and pavement licenses."
}];
const accreditations = ["NASC accredited", "CHAS compliant", "20+ years expertise"];
function HeroImageConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="h-full w-full bg-muted animate-pulse" />;
  }
  const matchedPhoto = posts.find(p => {
    const titleMatch = p.title?.toLowerCase() === "clapham scaffolding hero image";
    const nameMatch = p.customFields?.photo_name?.toLowerCase() === "clapham scaffolding hero image";
    return titleMatch || nameMatch;
  });
  const photoImage = matchedPhoto?.customFields?.image || matchedPhoto?.featuredImage;
  const photoAlt = matchedPhoto?.customFields?.alt_text || "BMF Scaffolding on commercial and residential site in Clapham";
  if (photoImage) {
    const imageObj = {
      src: photoImage,
      srcset: "",
      sizes: ""
    };
    return <img src={imageObj.src} data-wvc-srcset={imageObj.srcset} data-wvc-sizes={imageObj.sizes} alt={photoAlt} loading="lazy" decoding="async" className="h-full w-full object-cover" />;
  }
  return <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/clapham_scaffolding_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/clapham_scaffolding_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/clapham_scaffolding_hero.png-wvc-sizes" alt="BMF Scaffolding on commercial and residential site in Clapham" loading="lazy" decoding="async" className="h-full w-full object-cover" />;
}
export default function ClaphamHero() {
  const shouldReduceMotion = useReducedMotion();
  const sitePhotoQuery = new WP_Query({
    post_type: "site_photo",
    per_page: 20
  });
  return <section id="heroclaphamscaffolding" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-16 md:py-24 lg:py-28">
      {/* Background Subtle Gradient & Grid Texture */}
      <div className="absolute inset-0 bg-radial from-secondary-accent/40 via-secondary to-secondary pointer-events-none" />
      <div className="absolute inset-0 opacity-10 pointer-events-none bg-[linear-gradient(to_right,hsl(var(--secondary-foreground)/0.15)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--secondary-foreground)/0.15)_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Hero Copy */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="flex flex-wrap items-center gap-2 mb-6">
              <Badge variant="outline" className="border-secondary-foreground/30 text-secondary-foreground bg-secondary-accent/50 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                South West London &amp; Clapham
              </Badge>
              <span className="hidden sm:inline-block text-secondary-foreground/50 text-xs">
                |
              </span>
              <span className="text-xs text-secondary-foreground/80 font-medium">
                Safety-led access solutions
              </span>
            </motion.div>

            <motion.h1 initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.1,
            ease: "easeOut"
          }} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-secondary-foreground mb-6 font-default">
              Scaffolding in Clapham
            </motion.h1>

            <motion.p initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.2,
            ease: "easeOut"
          }} className="text-lg sm:text-xl text-secondary-foreground/85 leading-relaxed max-w-2xl mb-8">
              BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Clapham and the surrounding South West London area, including SW4.
            </motion.p>

            {/* CTAs */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.3,
            ease: "easeOut"
          }} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-7 text-base font-semibold shadow-md">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-secondary-foreground/30 text-secondary-foreground bg-secondary-accent/40 hover:bg-secondary-accent/80 hover:text-secondary-foreground h-12 px-6 text-base font-medium transition-[color,background-color,border-color]">
                <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 text-secondary-foreground hover:text-secondary-foreground">
                  <Phone className="size-4" />
                  <span>Call 01344 929900</span>
                </a>
              </Button>
            </motion.div>

            {/* Accreditations Row */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="pt-6 border-t border-secondary-border/60 w-full">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-secondary-foreground/80 font-medium">
                {accreditations.map((item, i) => <div data-index={i} key={item} className="flex items-center gap-1.5">
                    <ShieldCheck className="size-4 text-primary shrink-0" />
                    <span>{item}</span>
                  </div>)}
              </div>
            </motion.div>
          </div>

          {/* Right Column: Hero Visual & Service Grid */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.25,
            ease: "easeOut"
          }} className="relative overflow-hidden rounded-xl border border-secondary-border shadow-xl bg-secondary-accent/30 aspect-16/10 sm:aspect-2/1 lg:aspect-4/3">
              <PostsProvider wp_query={{
              post_type: "site_photo",
              per_page: 20,
              status: ["publish"]
            }} className="h-full w-full">
                <HeroImageConsumer />
              </PostsProvider>
              <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/20 to-transparent opacity-80 pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 text-xs text-secondary-foreground/90 bg-secondary/80 backdrop-blur-sm p-3 rounded-lg border border-secondary-border">
                <p className="font-semibold text-secondary-foreground">BMF Scaffolding Ltd</p>
                <p className="text-secondary-foreground/75">Serving Clapham, South West London, and the Home Counties</p>
              </div>
            </motion.div>

            {/* Quick Pillars 2x2 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {servicePillars.map((pillar, i) => {
              const IconComponent = pillar.icon;
              return <motion.div data-index={i} key={pillar.title} initial={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: 15
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.35 + i * 0.08
              }} className="p-3.5 rounded-lg bg-secondary-accent/40 border border-secondary-border hover:border-secondary-foreground/30 transition-[background-color,border-color]">
                    <div className="flex items-center gap-2 mb-1.5 text-secondary-foreground">
                      <IconComponent className="size-4 text-primary shrink-0" />
                      <h2 className="text-sm font-semibold font-default">{pillar.title}</h2>
                    </div>
                    <p className="text-xs text-secondary-foreground/75 leading-snug">
                      {pillar.description}
                    </p>
                  </motion.div>;
            })}
            </div>
          </div>

        </div>
      </div>
    </section>;
}